import type { Coordinates, ExpandedSlap } from '../utils/Primitives';
import { LngLat, LngLatBounds } from 'maplibre-gl'
import { BlockPlaces } from '@ivyroot/block-places';


// get the bounds of a square region of a given size centered on a point
// size is given in steps of 1/basis degrees
// NB: game is played in basis 100 which means latitude & longitude points are rounded to nearest 0.01 degree
export function enclosingAreaForPoint(center:  [number, number], basis: number, steps: number): Coordinates {

    // Helper function to round to nearest 0.01 degrees
    function roundToNearestBasis(value: number): number {
      return Math.round(value * basis) / basis;
    }
    const halfSide = roundToNearestBasis(steps * (1 / basis))

    let upperLeft: LngLat = new LngLat(
      roundToNearestBasis(center[0] + halfSide),
      roundToNearestBasis(center[1] - halfSide),
    )
    let upperRight: LngLat = new LngLat(
      roundToNearestBasis(center[0] + halfSide),
      roundToNearestBasis(center[1] + halfSide),
    )
    let lowerRight: LngLat = new LngLat(
      roundToNearestBasis(center[0] - halfSide),
      roundToNearestBasis(center[1] + halfSide),
    )
    let lowerLeft: LngLat = new LngLat(
      roundToNearestBasis(center[0] - halfSide),
      roundToNearestBasis(center[1] - halfSide),
    )

    const result: Coordinates = [
        upperLeft,
        upperRight,
        lowerRight,
        lowerLeft,
    ]

    return result
}


// return the height in degrees needed to make a square region of 0.01 degrees in longitude at a given latitude
// the returned region appears (roughly) square on a web mercator map
export function squareRegionHeight(bottomLatitude: number): number {
    // Convert degrees to radians
    const rad = (angle: number): number => angle * (Math.PI / 180);

    const latitudeRad = rad(bottomLatitude);

    // Compute delta latitude based on the formula derived
    const deltaLatitude = 0.000174532925199433 * (1.0 - Math.sin(latitudeRad)) * Math.tan(0.5 * latitudeRad + 0.785398163397448);

    // Convert result back to degrees
    return deltaLatitude * (180 / Math.PI);
}

// get the placement for a button showing on a place indefinitely, ie for pending slaps
export function coordinatesForPendingButton(bounds: LngLatBounds): [LngLat, LngLat, LngLat, LngLat] {
  const boundsNWCorner: LngLat = bounds.getNorthWest()

  const boundsWidth = bounds.getEast() - bounds.getWest();
  const insetWidth = boundsWidth * 0.05;
  const buttonWidth = boundsWidth * 0.7

  const boundsHeight = bounds.getNorth() - bounds.getSouth()
  const insetHeight = boundsHeight * 0.01;
  const buttonHeight = boundsHeight * 0.25

  // Calculate button coordinates
  const actionButtonCoordinates: [LngLat, LngLat, LngLat, LngLat] = [
      new LngLat(boundsNWCorner.lng + insetWidth, boundsNWCorner.lat - insetHeight),
      new LngLat(boundsNWCorner.lng + insetWidth + buttonWidth, boundsNWCorner.lat - insetHeight),
      new LngLat(boundsNWCorner.lng + insetWidth + buttonWidth, boundsNWCorner.lat - insetHeight - buttonHeight),
      new LngLat(boundsNWCorner.lng + insetWidth, boundsNWCorner.lat - insetHeight - buttonHeight),
  ];

  return actionButtonCoordinates;
}

export function coordinatesForInProgressLabelInPlace(bounds: LngLatBounds): [LngLat, LngLat, LngLat, LngLat] {
  const boundsWidth = bounds.getEast() - bounds.getWest();
  const boundsHeight = bounds.getNorth() - bounds.getSouth();
  const squareSize = Math.min(boundsWidth, boundsHeight) * 0.25;  // 25% of the smaller dimension

  const topRight: LngLat = bounds.getNorthEast();

  // Calculate inset
  const insetLng = boundsWidth * 0.02;  // 2% inset from the right edge
  const insetLat = boundsHeight * 0.02; // 2% inset from the top edge

  // Calculate square coordinates
  const squareCoordinates: [LngLat, LngLat, LngLat, LngLat] = [
      new LngLat(topRight.lng - insetLng - squareSize, topRight.lat - insetLat),           // Top-left
      new LngLat(topRight.lng - insetLng, topRight.lat - insetLat),                        // Top-right
      new LngLat(topRight.lng - insetLng, topRight.lat - insetLat - squareSize),           // Bottom-right
      new LngLat(topRight.lng - insetLng - squareSize, topRight.lat - insetLat - squareSize) // Bottom-left
  ];

  return squareCoordinates;
}

export function checkExpandedSlapsForClick(clickLngLat: LngLat, pendingSlaps: ExpandedSlap[]): number {
    for (const slap of pendingSlaps) {
        const slapBounds = BlockPlaces.sizedLngLatBoundsFromPlaceId(slap.place.id, slap.size || 1)
        if (slapBounds) {
          const placeButtonCoordinates = coordinatesForPendingButton(slapBounds)
          const placeButtonBounds = new LngLatBounds(
                [placeButtonCoordinates[3].lng, placeButtonCoordinates[3].lat], // new southwestern point
                [placeButtonCoordinates[1].lng, placeButtonCoordinates[1].lat]  // new northeastern point
          )
          if (placeButtonBounds.contains(clickLngLat)) {
            return slap.id
          }
        }
    }
    return -1
}